const COMPONENT_NAME = 'custom-product-section-rendering';
const SHOPIFY_SECTION_PREFIX = 'shopify-section-';
const sourceSectionIdCache = new Map();

class CustomProductSectionRendering extends HTMLElement {
  #abortController;

  connectedCallback() {
    document.addEventListener('click', this.#handleProductLinkClick);

    if (this.dataset.loaded === 'true' || this.dataset.loading === 'true') return;

    this.#load();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    document.removeEventListener('click', this.#handleProductLinkClick);
  }

  #handleProductLinkClick = (event) => {
    if (!(event instanceof MouseEvent) || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    // Intelligems A/B tests hid the buy box; kept for reference in case testing resumes.
    // if (this.closest('.custom-page-modules')?.hasAttribute('data-intelligems')) return;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || this.contains(link)) return;
    // Only page content scrolls to the buy box. The Rebuy cart and other overlays live outside
    // #MainContent, and their product links should still go to the product page.
    if (!link.closest('#MainContent') || link.closest('#rebuy-cart, [class*="rebuy"], [data-rebuy-id]')) return;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;

    const productUrl = new URL(this.dataset.productUrl || '', window.location.origin);
    const linkUrl = new URL(link.href, window.location.origin);
    const normalizePathname = (pathname) => pathname.replace(/\/+$/, '') || '/';

    if (normalizePathname(linkUrl.pathname) !== normalizePathname(productUrl.pathname)) return;

    event.preventDefault();
    this.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  async #load() {
    const content = this.querySelector('[data-product-section-content]');
    const error = this.querySelector('[data-product-section-error]');
    const productUrl = this.dataset.productUrl;

    if (!(content instanceof HTMLElement) || !productUrl) {
      this.#showError(error);
      return;
    }

    this.dataset.loading = 'true';
    this.#abortController = new AbortController();

    try {
      const url = this.#buildProductUrl(productUrl);
      const normalizedSourceSectionId = await this.#getSourceSectionId(url);

      url.searchParams.set('section_id', normalizedSourceSectionId);

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Section Rendering request failed with status ${response.status}`);
      }

      const responseText = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceSection = responseDocument.getElementById(`${SHOPIFY_SECTION_PREFIX}${normalizedSourceSectionId}`);

      if (!(sourceSection instanceof HTMLElement)) {
        throw new Error(`Section Rendering response did not contain ${SHOPIFY_SECTION_PREFIX}${normalizedSourceSectionId}`);
      }

      const renderedSection = /** @type {HTMLElement} */ (sourceSection.cloneNode(true));
      this.#prepareSection(renderedSection);
      this.#insertSupplementFactsTrigger(renderedSection);
      content.replaceChildren(renderedSection);
      content.removeAttribute('hidden');

      this.#initializeRechargeWidgets(renderedSection);

      this.dataset.loaded = 'true';
      this.removeAttribute('aria-busy');
      error?.setAttribute('hidden', '');

      renderedSection.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: normalizedSourceSectionId },
        })
      );

      this.dispatchEvent(
        new CustomEvent('custom-page:product-section:load', {
          bubbles: true,
          detail: {
            moduleId: this.dataset.moduleId,
            productId: this.dataset.productId,
            sourceSectionId: normalizedSourceSectionId,
          },
        })
      );
    } catch (loadError) {
      if (loadError instanceof DOMException && loadError.name === 'AbortError') return;

      console.error('[Custom product section rendering]', loadError);
      this.#showError(error);
    } finally {
      delete this.dataset.loading;
    }
  }

  /** @param {string} productUrl */
  #buildProductUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    const currentUrl = new URL(window.location.href);

    for (const parameter of ['preview_key', 'preview_theme_id', '_fd', 'pb']) {
      const value = currentUrl.searchParams.get(parameter);
      if (value && !url.searchParams.has(parameter)) url.searchParams.set(parameter, value);
    }

    return url;
  }

  /** @param {URL} productUrl */
  async #getSourceSectionId(productUrl) {
    const cacheKey = `${productUrl.origin}${productUrl.pathname}`;
    const cachedSectionId = sourceSectionIdCache.get(cacheKey);
    if (cachedSectionId) return cachedSectionId;

    const response = await fetch(productUrl, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: this.#abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Product page request failed with status ${response.status}`);
    }

    const responseText = await response.text();
    const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
    const productInformation = responseDocument.querySelector('.product-information[data-section-id]');
    const sectionWrapper = responseDocument.querySelector(
      `[id^="${SHOPIFY_SECTION_PREFIX}template--"][id$="__main"]`
    );
    const sourceSectionId =
      productInformation?.getAttribute('data-section-id') || sectionWrapper?.id.replace(SHOPIFY_SECTION_PREFIX, '');

    if (!sourceSectionId) {
      throw new Error('Product page response did not contain a discoverable product section ID');
    }

    const normalizedSourceSectionId = sourceSectionId.replace(new RegExp(`^${SHOPIFY_SECTION_PREFIX}`), '');
    sourceSectionIdCache.set(cacheKey, normalizedSourceSectionId);

    return normalizedSourceSectionId;
  }

  /**
   * Avoid duplicate IDs and radio groups when more than one referenced-product
   * module is rendered inside the same custom-page section.
   * @param {HTMLElement} renderedSection
   */
  #prepareSection(renderedSection) {
    const namespace = `custom-page-product-${this.dataset.moduleId || this.dataset.productId || 'module'}`
      .replace(/[^a-zA-Z0-9_-]/g, '-');
    const idMap = new Map();

    const elementsWithIds = [renderedSection, ...renderedSection.querySelectorAll('[id]')];
    for (const element of elementsWithIds) {
      // Recharge's inline app-block code can reference its original IDs.
      if (element.closest('.recharge-subscription-widget')) continue;

      const currentId = element.id;
      if (!currentId) continue;

      const scopedId = `${currentId}-${namespace}`;
      idMap.set(currentId, scopedId);
      element.id = scopedId;
    }

    const referenceAttributes = ['for', 'aria-controls', 'aria-describedby', 'aria-labelledby', 'form'];
    for (const attribute of referenceAttributes) {
      for (const element of renderedSection.querySelectorAll(`[${attribute}]`)) {
        if (element.closest('.recharge-subscription-widget')) continue;

        const references = element.getAttribute(attribute)?.split(/\s+/) || [];
        const scopedReferences = references.map((reference) => idMap.get(reference) || reference);
        element.setAttribute(attribute, scopedReferences.join(' '));
      }
    }

    for (const anchor of renderedSection.querySelectorAll('a[href^="#"]')) {
      if (anchor.closest('.recharge-subscription-widget')) continue;

      const currentId = anchor.getAttribute('href')?.slice(1);
      if (currentId && idMap.has(currentId)) {
        anchor.setAttribute('href', `#${idMap.get(currentId)}`);
      }
    }

    for (const radio of renderedSection.querySelectorAll('input[type="radio"][name]')) {
      if (radio.closest('.recharge-subscription-widget')) continue;

      radio.name = `${radio.name}-${namespace}`;
    }

    // Keep the referenced product's schema off the custom page while leaving
    // other JSON data and the product page's own schema untouched.
    // Executable scripts parsed from a Section Rendering response are inert
    // when inserted. Recharge requires its app-block scripts to be replayed for
    // dynamically rendered widgets; remove every other executable script to
    // avoid re-running section-level/global setup code.
    for (const script of renderedSection.querySelectorAll('script')) {
      const scriptType = script.type.toLowerCase();
      if (scriptType === 'application/ld+json') {
        let schema;
        try {
          schema = JSON.parse(script.textContent || '');
        } catch {
          // Leave malformed JSON-LD to the existing script handling.
        }

        if (schema?.['@type'] === 'Product') {
          script.remove();
          continue;
        }
      }

      const isJson = scriptType.includes('json');
      const isRechargeWidgetScript = script.closest('.recharge-subscription-widget');

      if (!isJson && !isRechargeWidgetScript) script.remove();
    }
  }

  /** @param {HTMLElement} renderedSection */
  #insertSupplementFactsTrigger(renderedSection) {
    const modalId = this.dataset.supplementFactsModalId;
    if (!modalId) return;

    const details = renderedSection.querySelector('.product-details');
    if (!(details instanceof HTMLElement)) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-unstyled custom-page-supplement-facts__trigger';
    button.textContent = 'View Supplement Facts';
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-controls', modalId);
    button.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('bodi:openModal', { detail: { modalId } }));
    });

    const variantControls = details.querySelector('variant-picker, .alternate-product-picker, .sku-selector');
    const buyButtons = details.querySelector('.buy-buttons-block');
    if (variantControls) {
      variantControls.before(button);
    } else if (buyButtons) {
      buyButtons.before(button);
    } else {
      details.append(button);
    }
  }

  /**
   * Recharge's Subscription Widget app block contains scripts that must be
   * recreated after the block is inserted through the Section Rendering API.
   * Replacing the web component then gives it a fresh connected lifecycle.
   * @param {HTMLElement} renderedSection
   */
  #initializeRechargeWidgets(renderedSection) {
    for (const container of renderedSection.querySelectorAll('.recharge-subscription-widget')) {
      for (const oldScript of container.querySelectorAll('script')) {
        const newScript = document.createElement('script');

        for (const attribute of oldScript.attributes) {
          newScript.setAttribute(attribute.name, attribute.value);
        }

        newScript.textContent = oldScript.textContent;
        oldScript.replaceWith(newScript);
      }

      const widget = container.querySelector('recharge-subscription-widget');
      if (widget) widget.replaceWith(widget.cloneNode(true));
    }
  }

  /** @param {Element | null} error */
  #showError(error) {
    this.removeAttribute('aria-busy');
    this.querySelector('[data-product-section-content]')?.setAttribute('hidden', '');
    error?.removeAttribute('hidden');
  }
}

if (!customElements.get(COMPONENT_NAME)) {
  customElements.define(COMPONENT_NAME, CustomProductSectionRendering);
}
